import { Avatar, Badge } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { forwardRef } from 'react';

//
import type { CustomAvatarProps } from './types';

// ----------------------------------------------------------------------

const getCharAtName = (name: string) => name && name.charAt(0).toUpperCase();

const getColorByName = (name: string) => {
  if (['A', 'H', 'L', 'N', 'Q'].includes(getCharAtName(name))) return 'primary';
  if (['F', 'G', 'I', 'J', 'T'].includes(getCharAtName(name))) return 'info';
  if (['B', 'D', 'K', 'O', 'Y'].includes(getCharAtName(name))) return 'success';
  if (['E', 'P', 'R', 'S', 'U'].includes(getCharAtName(name))) return 'warning';
  if (['M', 'V', 'W', 'X', 'Z'].includes(getCharAtName(name))) return 'error';
  return 'default';
};

// ----------------------------------------------------------------------

const CustomAvatar = forwardRef<HTMLDivElement, CustomAvatarProps>(
  ({ BadgeProps, children, color, name = '', sx, ...other }, ref) => {
    const theme = useTheme();

    const charAtName = getCharAtName(name);

    const colorByName = getColorByName(name);

    const colr = color || colorByName;

    const renderContent =
      colr === 'default' ? (
        <Avatar ref={ref} sx={sx} {...other}>
          {name && charAtName}
          {children}
        </Avatar>
      ) : (
        <Avatar
          ref={ref}
          sx={{
            backgroundColor: theme.palette[colr]?.main,
            color: theme.palette[colr]?.contrastText,
            fontWeight: theme.typography.fontWeightMedium,
            ...sx,
          }}
          {...other}
        >
          {name && charAtName}
          {children}
        </Avatar>
      );

    return BadgeProps ? (
      <Badge
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        overlap="circular"
        {...BadgeProps}
      >
        {renderContent}
      </Badge>
    ) : (
      renderContent
    );
  }
);

CustomAvatar.displayName = 'CustomAvatar';

export default CustomAvatar;
